import { Contact } from '@features/contacts/types/contact'
import { ImportedInvoiceExtraction } from '@features/importedInvoices/types/importedInvoiceExtraction'
import { ImportedInvoiceReviewFormValues } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'
import { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { getLocalDateFromDateString } from '../../../../../../../utils/getLocalDateFromDateString'
import { getImportedInvoiceReviewLines } from './getImportedInvoiceReviewLines'

interface GetImportedInvoiceReviewFormDefaultValuesOptions {
  baseCurrencyId: string
  clientContact?: Contact
  extraction: ImportedInvoiceExtraction
  isClientAmbiguous: boolean
  taxRates: TaxRate[] | undefined
}

const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/

const getClient = (
  options: GetImportedInvoiceReviewFormDefaultValuesOptions,
): ImportedInvoiceReviewFormValues['client'] => {
  const { clientContact, extraction, isClientAmbiguous } = options
  const clientName = extraction.header?.client?.name ?? undefined

  if (clientContact) {
    return clientContact
  }

  // The extracted name would show as a selected client while matching several customers.
  if (isClientAmbiguous) {
    return null
  }

  return clientName ? { name: clientName } : null
}

const getCurrency = (baseCurrencyId: string, currency?: string | null): string => {
  const currencyCode = currency?.trim().toUpperCase()

  return currencyCode && CURRENCY_CODE_PATTERN.test(currencyCode) ? currencyCode : baseCurrencyId
}

const getDate = (date?: string | null): Date | null => getLocalDateFromDateString(date ?? undefined) ?? null

export const getImportedInvoiceReviewFormDefaultValues = (
  options: GetImportedInvoiceReviewFormDefaultValuesOptions,
): ImportedInvoiceReviewFormValues => {
  const { baseCurrencyId, extraction, taxRates } = options
  const { header } = extraction

  return {
    client: getClient(options),
    currency: getCurrency(baseCurrencyId, header?.currency),
    dueDate: getDate(header?.dueDate),
    issueDate: getDate(header?.issueDate),
    lines: getImportedInvoiceReviewLines(extraction, taxRates),
    referenceNumber: header?.invoiceNumber ?? '',
  }
}
